// What to tell a player about their own plan and subscription.
//
// ONE pure function, because the hard part here is not the layout — it is that
// there are TWO sources and they do not agree.
//
//   THE DEVICE  RevenueCat's `customerInfo`, live from the store, and the
//               authority (plans.py says so: the paywall deliberately does not
//               trust the server's copy for a store purchase). Native only —
//               there is no RevenueCat SDK in a browser.
//   THE ACCOUNT The columns on `/auth/me`, a COPY fed by the RevenueCat
//               webhook. Available everywhere, including the website, and the
//               only answer there is on a lapsed or missed event.
//
// The device wins where we have it, the account answers where we do not, and
// the panel says which one it read — because "expires 3 March" from a webhook
// that missed a renewal is a different claim from the same sentence read off
// the store a second ago.
//
// A third case is neither: a plan granted BY HAND (`plan_source: 'manual'`, or
// a system admin), which has no store behind it and therefore nothing to
// cancel, restore or manage.

// `store` as RevenueCat reports it on a live entitlement.
const RC_STORE_TO_SOURCE = {
  APP_STORE: 'apple',
  MAC_APP_STORE: 'apple',
  PLAY_STORE: 'google',
  AMAZON: 'amazon',
  STRIPE: 'stripe',
  RC_BILLING: 'stripe',
  PROMOTIONAL: 'promotional',
};

// Said the way a player would say it, not the way the column spells it.
export const STORE_LABELS = {
  apple: 'Apple App Store',
  google: 'Google Play',
  amazon: 'Amazon Appstore',
  stripe: 'Card payment',
  promotional: 'Promotional',
  iap: 'App store',
  manual: 'Granted by 1-19',
};

// Where each store lets someone manage or cancel a subscription. Only used as
// a FALLBACK: RevenueCat hands us `managementURL`, which is already the right
// page for the right store and, on Android, already carries the product id.
export const STORE_MANAGE_URLS = {
  apple: 'https://apps.apple.com/account/subscriptions',
  google: 'https://play.google.com/store/account/subscriptions',
};

const PRO = 'pro';

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDay = (value) => {
  const d = parseDate(value);
  return d ? d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' }) : null;
};

// The entitlement this app sells. Named first, then any active one — the same
// order SubscriptionContext unlocks on, so the panel can never describe a
// subscription the app is meanwhile treating as absent.
const PRO_ENTITLEMENT = '1to19golf Pro';

export const activeEntitlement = (customerInfo) => {
  const active = customerInfo?.entitlements?.active;
  if (!active) return null;
  return active[PRO_ENTITLEMENT] || Object.values(active)[0] || null;
};

/**
 * @param {object}  profile       the `user` from /auth/me
 * @param {object}  customerInfo  RevenueCat's, or null
 * @param {boolean} isNative      running inside the iOS/Android shell
 * @param {boolean} isBypassed    admin or hand-granted Pro (plans.py)
 * @param {Date}    now           injected so the lapsed/active split is testable
 */
export const buildAccountVM = ({
  profile = {},
  customerInfo = null,
  isNative = false,
  isBypassed = false,
  now = new Date(),
} = {}) => {
  const ent = isNative ? activeEntitlement(customerInfo) : null;
  const fromDevice = !!ent;

  // `effective_plan` is the server's own answer with expiry applied.
  // `plan_type` is the stored column and goes on saying 'pro' after a lapse, so
  // it is never read on its own — the fallback only matters for a profile
  // cached before the server sent the better key.
  const accountPlan = profile.effective_plan || profile.plan_type || 'free';
  const isPro = fromDevice ? true : accountPlan === PRO;

  const source = fromDevice ? RC_STORE_TO_SOURCE[ent.store] || 'iap'
    : profile.plan_source || null;
  // A hand-granted plan is not a purchase. It is also the one case where the
  // account copy is the authority rather than a stale echo of the store.
  const complimentary = isPro && (isBypassed || source === 'manual' || (!source && !!profile.is_system_admin));

  const startedAt = fromDevice ? ent.originalPurchaseDate : profile.subscription_purchased_at;
  const expiresAt = fromDevice ? ent.expirationDate : profile.plan_expiry_date;
  const willRenew = fromDevice ? ent.willRenew : profile.subscription_will_renew;
  const periodType = String(fromDevice ? ent.periodType : profile.subscription_period_type || '').toUpperCase();
  const billingIssueAt = fromDevice ? ent.billingIssueDetectedAt : profile.subscription_billing_issue_at;
  const cancelledAt = fromDevice ? ent.unsubscribeDetectedAt : profile.subscription_unsubscribed_at;

  const expiry = parseDate(expiresAt);
  // LAPSED is not a kind of Pro — it is what the server already reports as
  // free, with an expiry date behind it that has passed. Testing it the other
  // way round (pro AND expired) can never be true, because `effective_plan`
  // has applied that very expiry before the client sees it.
  //
  // A MISSING expiry is not an expired one: a hand-granted plan has no end
  // date by design, so `null` is lifetime and stays Pro.
  const lapsed = !isPro && !!expiry && expiry <= now;
  const inTrial = periodType === 'TRIAL';

  const status = lapsed ? 'lapsed'
    : !isPro ? 'free'
      : complimentary ? 'complimentary'
        : billingIssueAt ? 'billing_issue'
          : willRenew === false ? 'cancelling'
            : inTrial ? 'trial'
              : 'active';

  // A subscription that HAS lapsed still has particulars worth showing — when
  // it ran out, and which store to go to about it. "Free" with nothing behind
  // it (someone who never subscribed) has none.
  const hasSubscription = isPro || lapsed;

  const STATUS = {
    free: { label: 'Free', tone: 'neutral' },
    lapsed: { label: 'Expired', tone: 'warn' },
    complimentary: { label: 'Complimentary', tone: 'good' },
    billing_issue: { label: 'Payment problem', tone: 'warn' },
    cancelling: { label: 'Cancels at period end', tone: 'warn' },
    trial: { label: 'Free trial', tone: 'good' },
    active: { label: 'Active', tone: 'good' },
  };

  // The date line is one fact under two different headings, and which heading
  // it gets is the whole message: the same 3 March is a renewal on a live
  // subscription and a deadline on a cancelled one.
  const expiryLabel = status === 'cancelling' || status === 'lapsed' ? 'Access until'
    : status === 'trial' ? 'Trial ends'
      : status === 'billing_issue' ? 'Access until'
        : 'Renews';

  const rows = [];
  if (hasSubscription && startedAt) {
    rows.push({ key: 'started', label: 'Started', value: formatDay(startedAt) });
  }
  if (hasSubscription && expiry) {
    rows.push({ key: 'expiry', label: expiryLabel, value: formatDay(expiresAt) });
  } else if (isPro && complimentary) {
    rows.push({ key: 'expiry', label: 'Expires', value: 'No end date' });
  }
  if (hasSubscription && source) {
    rows.push({ key: 'store', label: 'Purchased through', value: STORE_LABELS[source] || source });
  }

  // What the player is told to do about it. Neither Apple nor Google lets an
  // app cancel a subscription itself — the only honest action is to open the
  // store's own subscriptions page, which is also where a cancelled one is
  // resubscribed. So there is one button, and it does not say "Cancel".
  const storeBacked = hasSubscription && !complimentary
    && (source === 'apple' || source === 'google' || source === 'iap');
  const manageUrl = (fromDevice && customerInfo?.managementURL)
    || STORE_MANAGE_URLS[source] || null;

  return {
    isPro,
    status,
    statusLabel: STATUS[status].label,
    tone: STATUS[status].tone,
    planLabel: isPro && !lapsed ? 'Pro' : 'Free',
    complimentary,
    source,
    storeLabel: source ? (STORE_LABELS[source] || source) : null,
    // Which of the two sources this panel is describing, so it can say so.
    readFrom: fromDevice ? 'device' : 'account',
    rows,
    cancelledAt: cancelledAt || null,
    billingIssueAt: billingIssueAt || null,
    canManage: !!(storeBacked && manageUrl),
    manageUrl: storeBacked ? manageUrl : null,
    // Restoring asks the STORE what this Apple ID or Google account owns, so it
    // needs the SDK and therefore the app. It is offered whether or not we
    // think they are subscribed — a subscription the webhook missed is exactly
    // what it is for.
    canRestore: isNative,
  };
};

export default buildAccountVM;
